import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { clearDiscordCreditsNotice, readDiscordCreditsNotice } from './discordCreditsNotice';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { useLogout } from '@/Domain/hooks/Auth/useLogout';
import { useUser } from '@/Domain/hooks/User/useUser.hook';

const TOAST_ID = 'discord-credits-unavailable';
const TITLE_ID = 'discord-credits-unavailable-title';

// A toast, not a banner: the DefaultLayout banner slot is behind `!chromeHidden` and past the guard.
export const useDiscordCreditsNotice = (): void => {
  const { t } = useTranslation('subscription_page');
  const { user } = useUser();
  const { handleLogout } = useLogout();
  // Read once: dismissing clears the cookie, and a live re-read would fight that.
  const [notice] = useState(readDiscordCreditsNotice);
  const shownRef = useRef(false);
  const email = user?.email;

  useEffect(() => {
    if (!notice || !email || shownRef.current) return;
    shownRef.current = true;

    const dismiss = () => {
      clearDiscordCreditsNotice();
      toast.dismiss(TOAST_ID);
    };

    // Custom + unstyled: sonner's own layout puts the actions inline and collapses the description.
    toast.custom(
      () => (
        <div
          role="group"
          aria-labelledby={TITLE_ID}
          data-testid="discord-credits-notice"
          className="pointer-events-auto w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card p-4 shadow-lg"
        >
          <div className="flex gap-3">
            <span
              aria-hidden="true"
              className="grid size-8 shrink-0 place-items-center rounded-lg bg-info-muted text-info"
            >
              <DiscordIcon className="size-4" style={{ fill: 'currentColor' }} />
            </span>
            <div className="min-w-0 flex-1">
              <p id={TITLE_ID} className="text-sm font-medium text-foreground">
                {t('discord_credits_unavailable.title')}
              </p>
              <p className="mt-1 break-words text-sm text-muted-foreground">
                {t('discord_credits_unavailable.description', { email })}
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              data-testid="discord-credits-notice-dismiss"
            >
              {t('discord_credits_unavailable.dismiss')}
            </Button>
            <Button
              variant="dark"
              size="sm"
              // Leaves the cookie on purpose: the return hit counts it, then the view retires it.
              onClick={() => handleLogout(notice.returnPath ?? undefined)}
              data-testid="discord-credits-switch-account"
            >
              {t('discord_credits_unavailable.switch_account')}
            </Button>
          </div>
        </div>
      ),
      // onDismiss covers the swipe gesture, which would otherwise hide the toast and leave the
      // cookie behind for the notice to reappear on the next load.
      { id: TOAST_ID, duration: Infinity, unstyled: true, onDismiss: clearDiscordCreditsNotice },
    );
  }, [notice, email, handleLogout, t]);
};
